import * as THREE from 'three';
import { M, V } from '../prims.ts';
import type { Rng } from '../rand.ts';

/*
 * Shared shaping helpers for the era IV nightmares (sentinel, frost colossus, storm herald, shell serpent):
 * craggy lumps, smooth swells, ragged cloth panels, jagged tubes and crystal shards. All low-poly; every
 * random detail draws from the model's own seeded rng.
 */

export interface Kit {
  /** A craggy lump: an icosahedron with its corners pushed in and out (shared corners move together). */
  lump(parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, spin?: number, detail?: number, amt?: number): THREE.Mesh;
  /** A smooth swell (a muscle, a puff of cloud): a low sphere, scaled. */
  swell(parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, w?: number, h?: number): THREE.Mesh;
  /** A ragged hanging panel: top edge at `top`, `w` wide, `h` long, curving round by `bend`, facing ±Z. */
  panel(parent: THREE.Object3D, top: THREE.Vector3, w: number, h: number, bend: number, faceZ: number, mat: THREE.Material, ragged?: number): THREE.Mesh;
  /** A thin jagged tube along points (a seam, a vein of lightning). */
  crack(parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material, jit?: number): THREE.Mesh;
  /** A smooth tube along points (a tendril, a root). */
  tube(parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material, radial?: number): THREE.Mesh;
  /** A crystal shard: a stretched octahedron from `base` along `dir`, `len` long and `r` thick. */
  shard(parent: THREE.Object3D, mat: THREE.Material, base: THREE.Vector3, dir: THREE.Vector3, len: number, r: number): THREE.Mesh;
}

const UP = V(0, 1, 0);

export const kit = (rng: Rng): Kit => {
  const crag = (detail: number, amt: number): THREE.BufferGeometry => {
    const geo = new THREE.IcosahedronGeometry(1, detail);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const seen = new Map<string, number>();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
      let d = seen.get(key);
      if (d === undefined) {
        d = 1 + (rng() - 0.5) * amt;
        seen.set(key, d);
      }
      pos.setXYZ(i, x * d, y * d, z * d);
    }
    geo.computeVertexNormals();
    return geo;
  };
  return {
    lump: (parent, mat, x, y, z, sx, sy, sz, spin = 0.5, detail, amt) => {
      const big = detail ?? (Math.max(sx, sy, sz) > 0.3 ? 1 : 0);
      const m = M(crag(big, amt ?? (big ? 0.2 : 0.28)), mat, parent);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      m.rotation.set((rng() - 0.5) * spin, rng() * 6.3, (rng() - 0.5) * spin);
      return m;
    },
    swell: (parent, mat, x, y, z, sx, sy, sz, w = 7, h = 5) => {
      const m = M(new THREE.SphereGeometry(1, w, h), mat, parent);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      return m;
    },
    panel: (parent, top, w, h, bend, faceZ, mat, ragged = 0.38) => {
      const geo = new THREE.PlaneGeometry(w, h, 4, 3);
      const pos = geo.attributes.position as THREE.BufferAttribute;
      const cut: number[] = [];
      for (let i = 0; i <= 4; i++) cut.push(rng() * h * ragged * (i % 2 ? 1 : 0.5));
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        let y = pos.getY(i) - h / 2;
        const col = Math.round((x / w + 0.5) * 4);
        if (y < -h + 0.001) y += cut[col] ?? 0;
        const z = -bend * (x / w) ** 2 * 4 + (rng() - 0.5) * 0.02 - (y / h) * 0.02;
        pos.setXYZ(i, x, y, z * faceZ);
      }
      geo.computeVertexNormals();
      const m = M(geo, mat, parent);
      m.position.copy(top);
      return m;
    },
    crack: (parent, pts, r, mat, jit = 0.035) => {
      const pp: THREE.Vector3[] = [];
      pts.forEach((p, i) => {
        const q = pts[i + 1];
        pp.push(p);
        if (q) pp.push(p.clone().lerp(q, 0.5).add(V((rng() - 0.5) * jit, (rng() - 0.5) * jit, (rng() - 0.5) * jit)));
      });
      return M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pp, false, 'catmullrom', 0.05), Math.max(4, pp.length - 2), r, 3), mat, parent);
    },
    tube: (parent, pts, r, mat, radial = 4) => M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), pts.length * 2, r, radial), mat, parent),
    shard: (parent, mat, base, dir, len, r) => {
      const geo = new THREE.OctahedronGeometry(1, 0);
      geo.scale(r, len / 2, r * (0.7 + rng() * 0.4));
      geo.translate(0, len / 2, 0);
      const m = M(geo, mat, parent);
      m.position.copy(base);
      m.quaternion.setFromUnitVectors(UP, dir.clone().normalize());
      m.rotateY(rng() * 6.3);
      return m;
    },
  };
};

/**
 * A fork of lightning: an angular polyline (straight steps, not a smooth curve) with a branch off one of its
 * joints, whose joints are re-thrown every step (stop-motion crackle, baked into the boil frames).
 */
export class Fork {
  readonly mesh: THREE.Mesh;
  constructor(
    parent: THREE.Object3D,
    private readonly pts: THREE.Vector3[],
    private readonly rad: number,
    mat: THREE.Material,
    private readonly rng: Rng,
    private readonly jit = 0.05,
  ) {
    this.mesh = M(this.geo(), mat, parent);
  }
  private geo(): THREE.BufferGeometry {
    const R = (): number => (this.rng() - 0.5) * this.jit * 2;
    const p = this.pts.map((v, i) => (i === 0 ? v.clone() : v.clone().add(V(R(), R(), R()))));
    const path = (q: THREE.Vector3[]): THREE.CurvePath<THREE.Vector3> => {
      const c = new THREE.CurvePath<THREE.Vector3>();
      for (let i = 0; i + 1 < q.length; i++) c.add(new THREE.LineCurve3(q[i] as THREE.Vector3, q[i + 1] as THREE.Vector3));
      return c;
    };
    const main = new THREE.TubeGeometry(path(p), (p.length - 1) * 2, this.rad, 3, false);
    // A branch off a middle joint, half as thick, kinked once.
    const at = p[1 + Math.floor(this.rng() * Math.max(1, p.length - 2))] as THREE.Vector3;
    const end = p[p.length - 1] as THREE.Vector3;
    const d = end.clone().sub(p[0] as THREE.Vector3).multiplyScalar(0.3);
    const b1 = at.clone().add(d.clone().multiplyScalar(0.5)).add(V(R() * 2, R() * 2, R() * 2));
    const b2 = b1.clone().add(d.clone().multiplyScalar(0.6)).add(V(R() * 2, R() * 2, R() * 2));
    const branch = new THREE.TubeGeometry(path([at, b1, b2]), 4, this.rad * 0.6, 3, false);
    const merged = new THREE.BufferGeometry();
    const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
    for (const gg of [main, branch]) {
      const base = pos.length / 3;
      pos.push(...(gg.attributes.position as THREE.BufferAttribute).array);
      nor.push(...(gg.attributes.normal as THREE.BufferAttribute).array);
      uv.push(...(gg.attributes.uv as THREE.BufferAttribute).array);
      for (const i of gg.index?.array ?? []) idx.push(i + base);
      gg.dispose();
    }
    merged.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    merged.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    merged.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    merged.setIndex(idx);
    return merged;
  }
  step(): void {
    this.mesh.geometry.dispose();
    this.mesh.geometry = this.geo();
  }
}
